/**
 * Per-app Overview tile picker (§36). Lives in App Settings →
 * "Overview tiles" sub-tab. The user picks for one app:
 *   - a pin flag (Spend + Installs auto-occupy the first two slots)
 *   - up to 4 (pin on) or 6 (pin off) tile slots, each carrying an
 *     event_type + event_source + metric_kind + optional label.
 *
 * `available_events` (from the GET response) is the dropdown source —
 * only events that have actually landed in subscription_events or
 * adjust_events_raw for the last 30 days surface here. Empty list
 * means "connect a provider first".
 *
 * Saving fires `useUpsertOverviewTilesConfig`, which invalidates both
 * the editor's GET and `/api/stats/overview` so the Overview page
 * re-renders with the new tile mix on the next visit.
 */

import { useEffect, useMemo, useState } from "react";
import { Btn } from "@/components/Btn";
import { StatusToast } from "@/components/StatusToast";
import { useOrgPath } from "@/lib/useOrgNav";
import {
  useOverviewTilesConfig,
  useUpsertOverviewTilesConfig,
} from "@/lib/api-hooks";
import type {
  AvailableEvent,
  EventSource,
  MetricKind,
  OverviewTileSlot,
} from "@/lib/types";

const MAX_SLOTS = 6;
const PINNED_OFFSET = 2; // Spend + Installs occupy display positions 0 + 1

// Local draft slot mirrors the wire shape but allows an "empty" row
// (everything but slot_index unset) so the user can pick fields in
// any order. We only ship non-empty rows to the backend on Save.
type DraftSlot = {
  slot_index: number;
  event_type: string;
  event_source: EventSource | "";
  metric_kind: MetricKind;
  label: string;
  denominator_event_type: string;
  denominator_event_source: EventSource | "";
};

function emptyDraft(slot_index: number): DraftSlot {
  return {
    slot_index,
    event_type: "",
    event_source: "",
    metric_kind: "count",
    label: "",
    denominator_event_type: "",
    denominator_event_source: "",
  };
}

function tilesToDrafts(tiles: OverviewTileSlot[]): DraftSlot[] {
  const out: DraftSlot[] = [];
  for (let i = 0; i < MAX_SLOTS; i += 1) {
    const existing = tiles.find((t) => t.slot_index === i);
    if (existing) {
      out.push({
        slot_index: i,
        event_type: existing.event_type,
        event_source: existing.event_source,
        metric_kind: existing.metric_kind,
        label: existing.label ?? "",
        denominator_event_type: existing.denominator_event_type ?? "",
        denominator_event_source:
          existing.denominator_event_source ?? "",
      });
    } else {
      out.push(emptyDraft(i));
    }
  }
  return out;
}

/** Drop the placeholder rows (event_type unset OR event_source unset)
 *  and project to the wire shape. slot_index is preserved so the
 *  Overview page can reason about pin offsets stably. */
function draftsToTiles(drafts: DraftSlot[]): OverviewTileSlot[] {
  return drafts
    .filter((d) => d.event_type && d.event_source)
    .filter(
      (d) =>
        d.metric_kind !== "conversion" ||
        (d.denominator_event_type && d.denominator_event_source),
    )
    .map((d) => ({
      slot_index: d.slot_index,
      event_type: d.event_type,
      event_source: d.event_source as EventSource,
      metric_kind: d.metric_kind,
      label: d.label.trim() ? d.label.trim() : null,
      denominator_event_type:
        d.metric_kind === "conversion" ? d.denominator_event_type : null,
      denominator_event_source:
        d.metric_kind === "conversion"
          ? (d.denominator_event_source as EventSource)
          : null,
    }));
}

/** Group `available_events` by source so the dropdown can render
 *  `<optgroup>`s. Order inside each group: highest count_30d first
 *  so the user sees their dominant events near the top. */
function groupEvents(
  events: AvailableEvent[],
): Record<EventSource, AvailableEvent[]> {
  const out: Record<EventSource, AvailableEvent[]> = {
    adapty: [],
    revenuecat: [],
    adjust: [],
    asa: [],
  };
  for (const ev of events) out[ev.event_source].push(ev);
  for (const k of Object.keys(out) as EventSource[]) {
    out[k].sort((a, b) => b.count_30d - a.count_30d);
  }
  return out;
}

function fmtThousands(n: number): string {
  return n.toLocaleString("en-US");
}

/** Encode a (source, event_type) pair into a single `<select>` value
 *  so the dropdown can carry both. event_type can contain dots /
 *  underscores but never `|`, so this is unambiguous. */
function encodeOption(src: EventSource, evType: string): string {
  return `${src}|${evType}`;
}

function decodeOption(
  v: string,
): { source: EventSource; event_type: string } | null {
  const idx = v.indexOf("|");
  if (idx < 0) return null;
  const src = v.slice(0, idx) as EventSource;
  const evType = v.slice(idx + 1);
  if (!evType) return null;
  return { source: src, event_type: evType };
}

export function OverviewTilesEditor({ appId }: { appId: number }) {
  const cfgQuery = useOverviewTilesConfig(appId);
  const upsert = useUpsertOverviewTilesConfig();
  const orgPath = useOrgPath();

  // Local draft state — diverges from server state while the user
  // edits, snaps back on Save success (server returns the new state
  // and we re-seed from it).
  const [pinSpendInstalls, setPinSpendInstalls] = useState<boolean>(true);
  const [drafts, setDrafts] = useState<DraftSlot[]>(() =>
    tilesToDrafts([]),
  );
  const [toast, setToast] = useState<{
    msg: string;
    variant: "success" | "error";
  } | null>(null);

  // Reseed from server payload whenever the loaded config changes —
  // includes both first-load and post-save (when the mutation's
  // invalidate triggers a refetch). Key on `app_id` to also reseed
  // when the user switches apps in the parent picker. `cfg.tiles` is
  // a fresh array reference every refetch (TanStack's default
  // structural sharing notwithstanding — array element identity isn't
  // guaranteed across refetches when slot order changes), so listing
  // it satisfies exhaustive-deps without duplicate-reseed surprises.
  const cfg = cfgQuery.data;
  useEffect(() => {
    if (!cfg) return;
    setPinSpendInstalls(cfg.pin_spend_installs);
    setDrafts(tilesToDrafts(cfg.tiles));
  }, [cfg]);

  // Auto-dismiss the toast after a short window. Same pattern as
  // Overview's "Dashboard updated" toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const grouped = useMemo(
    () => groupEvents(cfg?.available_events ?? []),
    [cfg?.available_events],
  );

  // Map (source, event_type) → AvailableEvent for quick lookup —
  // used to surface count_30d next to a picked option and as the
  // primary "do we know about this event" signal.
  const eventLookup = useMemo(() => {
    const m = new Map<string, AvailableEvent>();
    for (const ev of cfg?.available_events ?? []) {
      m.set(encodeOption(ev.event_source, ev.event_type), ev);
    }
    return m;
  }, [cfg?.available_events]);

  const visibleCount = pinSpendInstalls ? 4 : MAX_SLOTS;
  // Per spec: when the pin flag is ON, slot indices 0..3 correspond
  // to display positions 2..5 (after Spend/Installs); when OFF, the
  // full 0..5 row range is editable. Either way we slice from the
  // start of `drafts` — slot_index is just a stable position label.
  const visibleDrafts = drafts.slice(0, visibleCount);

  const updateDraft = (i: number, patch: Partial<DraftSlot>) => {
    setDrafts((prev) =>
      prev.map((d, j) => (j === i ? { ...d, ...patch } : d)),
    );
  };

  const clearSlot = (i: number) => {
    setDrafts((prev) =>
      prev.map((d, j) => (j === i ? emptyDraft(d.slot_index) : d)),
    );
  };

  const onSave = async () => {
    try {
      await upsert.mutateAsync({
        app_id: appId,
        body: {
          pin_spend_installs: pinSpendInstalls,
          tiles: draftsToTiles(drafts),
        },
      });
      setToast({ msg: "Saved", variant: "success" });
    } catch (e) {
      setToast({
        msg: `Save failed: ${(e as Error).message}`,
        variant: "error",
      });
    }
  };

  if (cfgQuery.isLoading) {
    return (
      <div className="card card-pad">
        <div className="muted" style={{ fontSize: 13 }}>
          Loading…
        </div>
      </div>
    );
  }
  if (cfgQuery.error) {
    return (
      <div className="card card-pad">
        <div className="muted" style={{ fontSize: 13 }}>
          Failed to load tile config. Try refreshing the page.
        </div>
      </div>
    );
  }

  const noEvents = (cfg?.available_events?.length ?? 0) === 0;

  return (
    <>
      {toast && <StatusToast message={toast.msg} variant={toast.variant} />}

      <div className="card" style={{ padding: 18 }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
            Overview tiles
          </div>
          <div
            className="muted"
            style={{ fontSize: 12.5, lineHeight: 1.5, maxWidth: 720 }}
          >
            Pick which events show up as KPI tiles on the Overview page
            for this app. Defaults — when nothing is picked — are
            Spend / Revenue / ROAS / Installs / Trials / CPA.
          </div>
        </div>

        {/* Pin Spend + Installs toggle */}
        <label
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            fontSize: 13,
            marginBottom: 14,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={pinSpendInstalls}
            onChange={(e) => setPinSpendInstalls(e.target.checked)}
          />
          <span>
            Pin <strong>Spend</strong> + <strong>Installs</strong> in
            the first two slots
          </span>
          <span className="muted" style={{ fontSize: 12 }}>
            ({pinSpendInstalls ? "4 configurable slots" : "6 configurable slots"})
          </span>
        </label>

        {noEvents ? (
          <div
            style={{
              padding: "16px 18px",
              borderRadius: 10,
              background: "var(--c-surface-sunk)",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            No events observed for this app in the last 30 days. Connect
            Adapty, RevenueCat, or Adjust to start seeing event options
            here:{" "}
            <a
              href={`${orgPath("/settings")}?tab=integrations`}
              style={{ color: "var(--c-accent-600)" }}
            >
              Settings → Integrations
            </a>
            .
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {visibleDrafts.map((d, i) => (
              <SlotRow
                key={d.slot_index}
                displayIndex={i + (pinSpendInstalls ? PINNED_OFFSET : 0)}
                draft={d}
                grouped={grouped}
                eventLookup={eventLookup}
                onChange={(patch) => updateDraft(i, patch)}
                onClear={() => clearSlot(i)}
              />
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <Btn
            variant="primary"
            disabled={upsert.isPending || noEvents}
            onClick={() => void onSave()}
          >
            {upsert.isPending ? "Saving…" : "Save"}
          </Btn>
        </div>
      </div>
    </>
  );
}

function SlotRow({
  displayIndex,
  draft,
  grouped,
  eventLookup,
  onChange,
  onClear,
}: {
  // 0-based position in the Overview KPI grid, used as the row label.
  // Reflects pinning — when Spend/Installs are pinned, the first
  // editable slot is "Tile 3" (display index 2).
  displayIndex: number;
  draft: DraftSlot;
  grouped: Record<EventSource, AvailableEvent[]>;
  eventLookup: Map<string, AvailableEvent>;
  onChange: (patch: Partial<DraftSlot>) => void;
  onClear: () => void;
}) {
  const selectedKey = draft.event_source
    ? encodeOption(draft.event_source as EventSource, draft.event_type)
    : "";
  const picked = selectedKey ? eventLookup.get(selectedKey) ?? null : null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto auto auto",
        gap: 10,
        alignItems: "center",
        padding: "10px 12px",
        background: "var(--c-surface-sunk)",
        borderRadius: 10,
      }}
    >
      <div
        style={{
          width: 52,
          fontSize: 12,
          color: "var(--c-text-3)",
          fontWeight: 500,
        }}
      >
        Tile {displayIndex + 1}
      </div>

      <select
        className="select"
        value={selectedKey}
        onChange={(e) => {
          const decoded = decodeOption(e.target.value);
          if (!decoded) {
            onChange({ event_source: "", event_type: "" });
            return;
          }
          onChange({
            event_source: decoded.source,
            event_type: decoded.event_type,
          });
        }}
        style={{ minWidth: 0 }}
      >
        <option value="">— pick event —</option>
        {(["asa", "adapty", "revenuecat", "adjust"] as EventSource[]).map((src) => {
          const items = grouped[src];
          if (items.length === 0) return null;
          return (
            <optgroup
              key={src}
              label={
                src === "asa"
                  ? "ASA performance"
                  : src === "adapty"
                    ? "Adapty"
                    : src === "revenuecat"
                      ? "RevenueCat"
                      : "Adjust"
              }
            >
              {items.map((ev) => (
                <option
                  key={`${src}|${ev.event_type}`}
                  value={encodeOption(src, ev.event_type)}
                >
                  {ev.label
                    ? ev.label
                    : `${ev.event_type} (${fmtThousands(ev.count_30d)})`}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>

      {/* metric_kind segment — three radios styled as a chip strip.
          Hidden for ASA performance metrics — they're single-value
          formulas (CPI / CTR / etc.) where metric_kind has no
          meaning. */}
      {draft.event_source !== "asa" && (
      <div
        style={{
          display: "inline-flex",
          gap: 0,
          border: "1px solid var(--c-border)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {(["count", "revenue", "cpa", "conversion"] as MetricKind[]).map((mk) => {
          const isPicked = draft.metric_kind === mk;
          return (
            <button
              key={mk}
              type="button"
              onClick={() => onChange({ metric_kind: mk })}
              aria-pressed={isPicked}
              style={{
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: isPicked ? 600 : 500,
                // High-contrast selected state — previously
                // `--c-acc-50` light bg + `--c-acc-700` text barely
                // showed on the off-white card background. Filled
                // accent + white text on the picked button reads
                // unambiguously even at a glance.
                background: isPicked
                  ? "var(--c-accent-600)"
                  : "transparent",
                color: isPicked ? "#fff" : "var(--c-text-2)",
                border: "none",
                borderRight: "1px solid var(--c-border)",
                cursor: "pointer",
                transition:
                  "background 0.12s ease, color 0.12s ease",
              }}
            >
              {mk === "count"
                ? "Count"
                : mk === "revenue"
                  ? "Revenue"
                  : mk === "cpa"
                    ? "CPA"
                    : "Conversion"}
            </button>
          );
        })}
      </div>
      )}

      {/* Conversion-ratio denominator picker (§40). Only visible when
          metric_kind=conversion. Re-uses the same `grouped` event
          list so users can pick any provider event OR an ASA
          countable (installs / taps / impressions). */}
      {draft.metric_kind === "conversion" && draft.event_source !== "asa" && (
        <select
          className="input"
          value={
            draft.denominator_event_source && draft.denominator_event_type
              ? encodeOption(
                  draft.denominator_event_source as EventSource,
                  draft.denominator_event_type,
                )
              : ""
          }
          onChange={(e) => {
            const decoded = decodeOption(e.target.value);
            if (!decoded) {
              onChange({
                denominator_event_source: "",
                denominator_event_type: "",
              });
              return;
            }
            onChange({
              denominator_event_source: decoded.source,
              denominator_event_type: decoded.event_type,
            });
          }}
          style={{ minWidth: 0 }}
          title="Denominator event for the conversion ratio"
        >
          <option value="">— pick denominator —</option>
          {(["asa", "adapty", "revenuecat", "adjust"] as EventSource[]).map(
            (src) => {
              const items = grouped[src];
              if (items.length === 0) return null;
              return (
                <optgroup
                  key={src}
                  label={
                    src === "asa"
                      ? "ASA performance"
                      : src === "adapty"
                        ? "Adapty"
                        : src === "revenuecat"
                          ? "RevenueCat"
                          : "Adjust"
                  }
                >
                  {items
                    .filter(
                      (ev) =>
                        // ASA-side denominator only meaningful for
                        // countables — exclude derived/cost metrics.
                        src !== "asa" ||
                        ev.event_type === "installs" ||
                        ev.event_type === "taps" ||
                        ev.event_type === "impressions",
                    )
                    .map((ev) => (
                      <option
                        key={`${src}|${ev.event_type}`}
                        value={encodeOption(src, ev.event_type)}
                      >
                        {ev.label
                          ? ev.label
                          : `${ev.event_type} (${fmtThousands(ev.count_30d)})`}
                      </option>
                    ))}
                </optgroup>
              );
            },
          )}
        </select>
      )}

      <input
        type="text"
        className="input"
        placeholder="Label (optional)"
        value={draft.label}
        onChange={(e) => onChange({ label: e.target.value })}
        maxLength={80}
        style={{ width: 180, fontSize: 13 }}
      />

      <button
        type="button"
        className="btn-ghost"
        onClick={onClear}
        disabled={!picked && !draft.label}
        title="Clear slot"
        style={{
          padding: "4px 8px",
          fontSize: 12,
          color: "var(--c-text-3)",
          background: "transparent",
          border: "1px solid transparent",
          cursor: picked || draft.label ? "pointer" : "default",
        }}
      >
        Clear
      </button>
    </div>
  );
}
